import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, Globe, Languages, Search, Bell, Filter, Users, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/SEOHead";
import LanguageSelector from "@/components/LanguageSelector";

const EU_LANGUAGES = [
  "Български",
  "Čeština",
  "Dansk",
  "Deutsch",
  "Eesti",
  "Ελληνικά",
  "English",
  "Español",
  "Français",
  "Gaeilge",
  "Hrvatski",
  "Italiano",
  "Latviešu",
  "Lietuvių",
  "Magyar",
  "Malti",
  "Nederlands",
  "Polski",
  "Português",
  "Română",
  "Slovenčina",
  "Slovenščina",
  "Suomi",
  "Svenska",
];

export default function MultilingualAccess() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* ✅ Single SEOHead (with alternates) */}
      <SEOHead
        title="Multilingual Access"
        description="Access EU tenders in all 24 official EU languages. Remove language barriers and monitor TED tenders with TED Monitor."
        path="/multilingual-access"
        alternates={[
          { lang: "en", href: "https://tedmonitor.eu/multilingual-access" },
          { lang: "it", href: "https://tedmonitor.eu/it/accesso-multilingue" },
          { lang: "x-default", href: "https://tedmonitor.eu/multilingual-access" },
        ]}
      />

      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-mark.png" alt="TED Monitor" className="h-8 w-8" />
            <span className="font-semibold text-lg text-primary">TED Monitor</span>
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSelector />
            <Button asChild>
              <Link to="/auth">Get Started</Link>
            </Button>
          </div>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="py-8 md:py-12 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <h1 className="text-3xl md:text-4xl font-bold text-primary mb-4 leading-tight">
              Multilingual access to European public procurement tenders
            </h1>

            <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-3">
              The EU procurement market is inherently multilingual. Tenders are published across Member States and
              languages, which can create friction for organizations working internationally.
            </p>

            <p className="text-base font-medium text-foreground">
              TED Monitor provides access to EU tenders in all 24 official EU languages, helping you monitor
              opportunities across borders with fewer language barriers.
            </p>

            {/* Internal links (SEO cluster) */}
            <div className="mt-6 flex flex-wrap gap-2">
              <Link
                to="/eu-tender-monitoring"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                EU tender monitoring
              </Link>
              <Link
                to="/ted-procurement-alerts"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                TED procurement alerts
              </Link>
              <Link
                to="/european-public-procurement-software"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                European procurement software
              </Link>
              <Link
                to="/compliance-and-security"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Compliance and security
              </Link>
              <Link
                to="/pricing"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Pricing
              </Link>
            </div>

            <p className="mt-4 text-[11px] text-muted-foreground">
              Official source:{" "}
              <a
                href="https://ted.europa.eu"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                TED (Tenders Electronic Daily)
              </a>{" "}
              – Supplement to the Official Journal of the European Union.
            </p>
          </div>
        </section>

        {/* 24 languages */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Languages className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Access EU tenders in all 24 official EU languages
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              The European Union recognizes 24 official languages, all of which may appear in procurement notices. With
              TED Monitor you can:
            </p>

            <ul className="space-y-1.5 text-muted-foreground text-sm mb-4">
              {[
                "Consult tenders published in any official EU language",
                "Review tender information without being limited to a single language ecosystem",
                "Operate across multiple EU countries with one centralized workflow",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            {/* Language badges */}
            <div className="flex flex-wrap gap-1.5">
              {EU_LANGUAGES.map((lang) => (
                <span
                  key={lang}
                  className="px-2 py-0.5 bg-primary/10 text-primary rounded-full text-[10px] font-medium"
                >
                  {lang}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Overcome barriers */}
        <section className="py-6 md:py-8 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Globe className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Overcome language barriers in public procurement
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              Language is one of the main obstacles to cross-border procurement discovery. Multilingual access helps
              you:
            </p>

            <ul className="space-y-1.5 text-muted-foreground text-sm mb-4">
              {[
                "Identify opportunities outside your domestic market",
                "Expand your addressable procurement pipeline across the EU",
                "Reduce the risk of missing relevant tenders due to language constraints",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <p className="text-muted-foreground leading-relaxed text-sm">
              Monitoring should not be limited by the publication language of the notice.
            </p>

            <p className="mt-4">
              <Link to="/eu-tender-monitoring" className="text-primary hover:underline font-medium text-sm">
                Explore EU tender monitoring →
              </Link>
            </p>
          </div>
        </section>

        {/* Cross-border */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Building2 className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Designed for cross-border procurement
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              EU procurement is increasingly cross-border. Multilingual access is a core requirement for:
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
              <div className="p-2 bg-muted/50 rounded-lg border text-center">
                <Users className="h-5 w-5 text-primary mx-auto mb-1" />
                <p className="font-medium text-foreground text-xs">Companies active in multiple EU markets</p>
              </div>
              <div className="p-2 bg-muted/50 rounded-lg border text-center">
                <Globe className="h-5 w-5 text-primary mx-auto mb-1" />
                <p className="font-medium text-foreground text-xs">International consultants</p>
              </div>
              <div className="p-2 bg-muted/50 rounded-lg border text-center">
                <Building2 className="h-5 w-5 text-primary mx-auto mb-1" />
                <p className="font-medium text-foreground text-xs">Professional firms</p>
              </div>
              <div className="p-2 bg-muted/50 rounded-lg border text-center">
                <Languages className="h-5 w-5 text-primary mx-auto mb-1" />
                <p className="font-medium text-foreground text-xs">Operators expanding into the EU</p>
              </div>
            </div>

            <p className="text-muted-foreground leading-relaxed text-sm">
              Multilingual access is not an optional feature—it is essential procurement infrastructure.
            </p>

            <p className="mt-4">
              <Link
                to="/european-public-procurement-software"
                className="text-primary hover:underline font-medium text-sm"
              >
                Explore European procurement software →
              </Link>
            </p>
          </div>
        </section>

        {/* Integrated */}
        <section className="py-6 md:py-8 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Search className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Multilingual access integrated with tender monitoring
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              Multilingual access is integrated into core monitoring workflows, including:
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
              <div className="p-2 bg-background rounded-lg border text-center">
                <Search className="h-5 w-5 text-primary mx-auto mb-1" />
                <p className="font-medium text-foreground text-xs">Tender search</p>
              </div>
              <div className="p-2 bg-background rounded-lg border text-center">
                <Filter className="h-5 w-5 text-primary mx-auto mb-1" />
                <p className="font-medium text-foreground text-xs">CPV filtering</p>
              </div>
              <div className="p-2 bg-background rounded-lg border text-center">
                <Bell className="h-5 w-5 text-primary mx-auto mb-1" />
                <p className="font-medium text-foreground text-xs">Automated alerts</p>
              </div>
              <div className="p-2 bg-background rounded-lg border text-center">
                <Globe className="h-5 w-5 text-primary mx-auto mb-1" />
                <p className="font-medium text-foreground text-xs">Market visibility</p>
              </div>
            </div>

            <p className="text-muted-foreground leading-relaxed text-sm">
              This supports consistent monitoring regardless of the tender&apos;s publication language.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-8 md:py-12 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h2 className="text-xl md:text-2xl font-semibold mb-4">Access EU tenders without language limits</h2>
            <p className="text-base mb-6 opacity-90">
              Start monitoring EU public procurement opportunities across countries and languages with one platform.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="default" variant="secondary" asChild>
                <Link to="/pricing" className="gap-2">
                  View pricing
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button
                size="default"
                variant="outline"
                className="bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                asChild
              >
                <Link to="/auth">Get started</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t py-8 bg-background">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src="/logo-mark.png" alt="TED Monitor" className="h-6 w-6" />
              <span className="text-sm text-muted-foreground">© {new Date().getFullYear()} TED Monitor</span>
            </div>
            <nav className="flex flex-wrap gap-6 text-sm">
              <Link to="/pricing" className="text-muted-foreground hover:text-foreground">
                Pricing
              </Link>
              <Link to="/eu-tender-monitoring" className="text-muted-foreground hover:text-foreground">
                EU Tender Monitoring
              </Link>
              <Link to="/ted-procurement-alerts" className="text-muted-foreground hover:text-foreground">
                Procurement Alerts
              </Link>
              <Link to="/european-public-procurement-software" className="text-muted-foreground hover:text-foreground">
                Procurement Software
              </Link>
              <Link to="/privacy-policy" className="text-muted-foreground hover:text-foreground">
                Privacy
              </Link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}
